// FILE: src/reputationClient.js
// Oy verme: /content/<id>/ratings/<uid>
// Not: Kök /content belgesini rater güncellemez. (Kurallara göre sadece author update edebilir.)

import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, writeBatch } from "firebase/firestore";
// PR-63 — cihaz-kalıcı "Puanın: X★" cache'i. YALNIZ başarılı canonical
// yazım/okuma sonrası güncellenir (optimistic fake yok); canonical kazanır.
import { setCachedMyRating, removeCachedMyRating } from "./utils/myRatingsCache";
import { hataAlani } from "./utils/hataAlani";
// `agg.avg` artık sunucuda SAKLANMIYOR (commutative yazım) → okuma anında türet.
import { aggNormalize } from "./utils/aggTuret";

/**
 * `content/{id}` belgesindeki içerik türü. JSDoc sözleşmesi bu üçünü sayar,
 * ama ÖLÇÜLDÜ: `StarRatingV2` prop'u `type = "post"` ile `string` genişliğinde
 * geliyor ve gövde yalnız `String(type)` yapıyor → daraltmak çağıranı yalanlar.
 * Bu yüzden bilinen değerler burada BELGELENİR, parametre `string` kalır.
 */
export type IcerikTuru = "post" | "story" | "clip";

/**
 * `content/{id}.agg` — alanlar TAHMİN DEĞİL, tüm OKUYUCULARDAN toplandı:
 * ClipsDesktop/MyClips (`avg,count,sum,bayes`), ProfilePostViewerMobile
 * (`avg,count`), myclipsv3/engagement/useClipEngagement (`total,n,average`).
 */
export type IcerikToplami = {
  avg?: number;
  count?: number;
  sum?: number;
  bayes?: number;
  total?: number;
  n?: number;
  average?: number;
};

/** `onContentAggregate` dinleyicisi. */
export type ToplamDinleyici = (agg: IcerikToplami | null) => void;

export const CONTENT_COL = "content";
export const RATINGS_SUBCOL = "ratings";

/** Aynı içeriğe yeniden puan verme aralığı (UX ön-kontrolü; asıl kapı rules). */
export const RERATE_COOLDOWN_MS = 30 * 1000;

/**
 * ⏱️ RE-RATE BEKLEMESİ — SAF, kırpmalı.
 *
 * ── ÖLÇÜLEN KUSUR (#931) ────────────────────────────────────────────────
 * Eski gövde `elapsed = Date.now() - prevMs` yazıyordu ve **negatif elapsed
 * KIRPILMIYORDU**. `prevMs` gelecekteyse (istemci saati geride, `updatedAt`
 * sunucu damgası ileride, cihaz saati elle oynatılmış) `elapsed < 0` olur ve
 * `waitS = ceil((COOLDOWN - elapsed)/1000)` **cooldown'ı AŞAR** — kullanıcıya
 * `RATING_COOLDOWN:20202506` (≈234 gün) gösterildi.
 *
 * ⚠️ Sanal dünyadaki o 234 günlük sayının KAYNAĞI dünyanın kendi saat
 * kaymasıydı (üretim kusuru değil). Üretim kusuru olan ve burada düzeltilen
 * şey ayrı ve dar: **kırpma yok** → bekleme üst sınırsız.
 *
 * DEĞİŞMEZ: dönen değer HER ZAMAN `0 .. ceil(cooldownMs/1000)` aralığında.
 * 0 = bekleme yok.
 */
export function rerateBeklemeSaniyesi(
  prevMs: number,
  simdiMs: number,
  cooldownMs: number = RERATE_COOLDOWN_MS,
): number {
  // `prevMs` yok/bozuk → hiç yazım olmamış say (eski gövdedeki `prevMs > 0`).
  if (!Number.isFinite(prevMs) || prevMs <= 0) return 0;
  if (!Number.isFinite(simdiMs)) return 0;
  // ⬇️ KIRPMA: saat kayması bekleme üretmez, yalnız "hiç zaman geçmedi" der.
  const gecen = Math.max(0, simdiMs - prevMs);
  if (gecen >= cooldownMs) return 0;
  return Math.max(1, Math.ceil((cooldownMs - gecen) / 1000));
}

/** İçerik belgesini sadece yoksa oluştur. (update yok!) */
export async function ensureContentDoc(
  contentId: string | null | undefined,
  authorId: string | null | undefined = "unknown",
  type: string = "post",
) {
  if (!contentId) throw new Error("ensureContentDoc: contentId gerekli.");
  const ref = doc(db, CONTENT_COL, String(contentId));
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // kurallar: create serbest (authorId string, type in ['post','story','clip'])
    await setDoc(ref, {
      authorId: String(authorId),
      type: String(type),
      createdAt: serverTimestamp(),
    });
  }
  return ref;
}

/**
 * Oy ver (yalnızca /ratings altına yazar)
 * @param {{contentId:string, authorId?:string, value:number, type?:'post'|'story'|'clip'}} p
 */
export async function rateContent({
  contentId,
  authorId = "unknown",
  value,
  type = "post",
}: {
  // ÖLÇÜLDÜ (ClipsDesktop:944, MyClips:1129): çağıranlar `selected.id`
  // (`string | undefined`) ve `onRate(deger: number | null)` geçiyor. Gövde
  // ikisini de aşağıdaki guard'larla ZATEN eliyor → tip çağıranı yalanlamaz.
  contentId: string | undefined;
  authorId?: string;
  value: number | null;
  /** Bilinen değerler: {@link IcerikTuru}. */
  type?: string;
}) {
  const uid = auth?.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated.");
  if (!contentId) throw new Error("contentId required.");
  // `value == null` dalı DAVRANIŞ EKLEMEZ: `Number.isInteger(null)` zaten
  // `false` → aynı hata, aynı mesaj. Yalnız daraltmayı derleyiciye gösterir.
  if (value == null || !Number.isInteger(value) || value < 1 || value > 5)
    throw new Error("value 1..5 olmalı.");

  // 🐞 BUGGIFY · i-yildiz-icerik-dokumani-yok — ÜST BELGEYİ HİÇ YAZMA.
  // Taklit edilen GERÇEK arıza: `ensureContentDoc` ayrı bir yazımdır ve
  // düşebilir (kota, rules, ağ) ya da eski bir istemci onu hiç çağırmayabilir.
  // O zaman `content/{id}/ratings/{uid}` VAR ama üst `content/{id}` YOKTUR.
  // SINANAN ONARIM SUNUCUDA: `onRatingWrite`in NOT_FOUND (gRPC 5) dalı —
  // yorumunun kendi sözü *"orphan agg-only doc yaratma"*. Yani zincir bu
  // durumda ÖKSÜZ bir `agg` üretmemeli ve fonksiyon PATLAMAMALI.
  // ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser bloğu siler).
  if (!(process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-yildiz-icerik-dokumani-yok"))) {
    await ensureContentDoc(contentId, authorId, type);
  }

  const ratingRef = doc(db, CONTENT_COL, String(contentId), RATINGS_SUBCOL, uid);

  // PR-12 — VELOCITY GUARD (UX katmanı; gerçek güvenlik rules'ta).
  // Varlık-farkındalı yazım: (1) create'te createdAt+updatedAt serverTimestamp
  // (rules == request.time zorunlu kılar); (2) re-rate'te createdAt GÖNDERİLMEZ
  // (rules immutable ister; merge mevcut değeri korur) + 30sn cooldown lokal
  // ÖN-KONTROL: kalan süre dostça toast'la söylenir, rules'a hiç çarpılmaz →
  // permission-denied spam'i olmaz. Ön-kontrol atlatılırsa rules yine keser.
  const prevSnap = await getDoc(ratingRef);
  // 🐞 BUGGIFY · i-yildiz-onceki-oy-gorunmez — MEVCUT OYU GÖRMEZDEN GEL.
  // Taklit edilen GERÇEK arıza: bu `getDoc` bayat/önbellekten dönebilir ya da
  // hesap değişimi yarışında boş gelebilir. Sonucu ölçülebilir: istemci
  // "ilk oy" yolunu seçer ve `createdAt: serverTimestamp()`ı MEVCUT belgeye
  // yeniden yazar. SINANAN ONARIM `firestore.rules`tur — dosyanın kendi
  // yorumu diyor ki *"re-rate'te createdAt GÖNDERİLMEZ (rules immutable
  // ister)"* ve *"ön-kontrol atlatılırsa rules yine keser"*. Bu nokta o
  // İKİNCİ HATTI gerçekten koşturur; bugüne kadar bir İDDİAYDI.
  // ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser bloğu siler).
  const prev = (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-yildiz-onceki-oy-gorunmez"))
    ? { exists: () => false, data: () => ({}) } as typeof prevSnap
    : prevSnap;
  if (prev.exists()) {
    const prevData = prev.data() || {};
    const prevMs = prevData.updatedAt?.toMillis?.() ?? 0;
    // #931 — bekleme SAF yardımcıdan gelir ve negatif `elapsed` KIRPILIR
    // (bkz. rerateBeklemeSaniyesi başlığı). Üst sınır: 30 sn.
    let waitS = rerateBeklemeSaniyesi(prevMs, Date.now(), RERATE_COOLDOWN_MS);
    // 🐞 BUGGIFY · i-yildiz-cooldown-atla — YEREL VALFİ AÇIK BIRAK.
    // Taklit edilen GERÇEK arıza: istemci saatinin kayması ya da `updatedAt`ın
    // bayat okunması → 30 sn'lik yerel ön-kontrol yanlış hesaplanır, valf
    // açılır ve yazım DOĞRUDAN `firestore.rules`a çarpar. Üstteki yorum
    // "ön-kontrol atlatılırsa rules yine keser" diyor; o cümle bugüne kadar
    // sanal dünyada HİÇ koşulmamış bir İDDİAYDI — bu nokta onu koşturur.
    // ⚙️ ÜRETİMDE DERLEME ZAMANI ELENİR: `process.env.NODE_ENV` DefinePlugin
    //    ile `"production"` sabitine iner → `false && …` → Terser bloğu siler.
    //    Kilit + paket taraması: src/uretimBuggifySizintiKapisi.test.ts.
    if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-yildiz-cooldown-atla")) {
      waitS = 0; // "bekleme dolmuş" gibi davran → valf açılır
    }
    if (waitS > 0) {
      try {
        const { showToast } = await import("./ToastBoot");
        showToast(`Puanını ${waitS} sn sonra tekrar değiştirebilirsin.`, { variant: "info" });
      } catch { /* toast yoksa sessiz */ }
      const err: any = new Error(`RATING_COOLDOWN:${waitS}`);
      err.code = "rating-cooldown";
      throw err;
    }
    // Aynı değeri tekrar yazmak anlamsız + cooldown'ı boşa harcamasın.
    if (Number(prevData.value) === value) {
      setCachedMyRating(uid, contentId, value); // canonical zaten bu değer
      return { ok: true, unchanged: true };
    }
    // PR-64 — canonical + user mirror TEK atomik batch (ikisi birlikte
    // başarır/başarısız olur; yarım-yazım drift'i yok). Mirror = display
    // katmanı (users/{uid}/myRatings/{contentId}, owner-only rules).
    {
      const batch = writeBatch(db);
      batch.set(
        ratingRef,
        { raterId: uid, value, updatedAt: serverTimestamp() },
        { merge: true }
      );
      batch.set(
        doc(db, "users", uid, "myRatings", String(contentId)),
        { value, updatedAt: serverTimestamp() },
        { merge: true }
      );
      await batch.commit();
    }
    setCachedMyRating(uid, contentId, value); // yalnız BAŞARILI commit sonrası
    return { ok: true };
  }

  // PR-64 — canonical + user mirror TEK atomik batch (bkz. yukarıdaki update
  // yolu). Canonical alanlar/serverTimestamp davranışı AYNEN korunur.
  const batch = writeBatch(db);
  batch.set(
    ratingRef,
    {
      raterId: uid,
      value,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );
  // 🐞 BUGGIFY · i-yildiz-ayna-yarim — AYNA YAZIMINI BATCH'TEN DÜŞÜR.
  // Taklit edilen GERÇEK arıza: atomik sandığımız yazımın YARIM inmesi —
  // canonical `content/{id}/ratings/{uid}` yazılır, kullanıcı aynası
  // `users/{uid}/myRatings/{id}` yazılmaz (rules reddi, kota, kısmi commit).
  // Sonucu sessizdir: "Puanın: X★" cihaz cache'i canonical'dan ayrışır ve
  // kullanıcı kendi puanını yanlış görür. Batch'in atomikliği bu ürünün
  // varsayımıdır; buggify onu bir turluğuna GERİ ALIP varsayımı sınar.
  // ⚙️ ÜRETİMDE DERLEME ZAMANI ELENİR (`false && …` → Terser bloğu düşürür;
  //    koşul silinir, ayna yazımı ÜRETİMDE HER ZAMAN yapılır).
  if (!(process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-yildiz-ayna-yarim"))) {
    batch.set(
      doc(db, "users", uid, "myRatings", String(contentId)),
      { value, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
  await batch.commit();

  setCachedMyRating(uid, contentId, value); // yalnız BAŞARILI commit sonrası
  return { ok: true };
}

/**
 * ÇEKİRDEK OKUMA — HATAYI YUTMAZ (EMİR #97 / A6).
 * `getMyRating` hatayı yutup 0 döndüğü için, "puan vermedim" ile "okuyamadım"
 * ayrımı ANCAK burada doğabilir. İlk yazımımda `getMyRatingDurum`u
 * `getMyRating` üzerine kurmuştum — ayrım hiç doğmuyordu, yani düzeltme
 * kâğıt üstündeydi. tsc yakaladı; kurgu düzeltildi.
 */
async function myRatingOku(contentId: string | null | undefined, uid: string | null = null) {
  const raterUid = uid || auth?.currentUser?.uid;
  if (!contentId || !raterUid) return 0;   // GERÇEK cevap: puan verilemez
  const ref = doc(db, CONTENT_COL, String(contentId), RATINGS_SUBCOL, String(raterUid));
  const snap = await getDoc(ref);
  const v = snap.exists() ? Number(snap.data()?.value) : 0;
  const out = Number.isInteger(v) && v >= 1 && v <= 5 ? v : 0;
  if (out >= 1) setCachedMyRating(raterUid, contentId, out);
  else removeCachedMyRating(raterUid, contentId);
  return out;
}

/**
 * A6 — ÜÇ DEĞERLİ SÜRÜM: `{ deger, durum }`.
 * `bilinmiyor` iken çağıran taraf mevcut değeri EZMEMELİ (0 yazmamalı) ve
 * toggle-off'u "puanım yok" varsayımıyla çalıştırmamalı — somut zarar
 * A5'inkiyle aynı şekil: kullanıcı puanını GERİ ALAMIYOR.
 */
export async function getMyRatingDurum(
  contentId: string | null | undefined,
  uid: string | null = null,
) {
  try {
    const deger = await myRatingOku(contentId, uid);
    return { deger, durum: deger >= 1 ? "evet" : "hayir" };
  } catch {
    return { deger: 0, durum: "bilinmiyor" };
  }
}

/** Kullanıcının bu içeriğe verdiği puanı oku (yoksa 0). myRating kalıcılığı —
 *  klibe geri dönünce/yeniden açınca verilen yıldız dolu görünsün diye.
 *  ⚠️ ESKİ SÖZLEŞME KORUNUYOR (hata → 0). Ayrım gereken yerde
 *  `getMyRatingDurum` kullanılmalı. */
export async function getMyRating(
  contentId: string | null | undefined,
  uid: string | null = null,
) {
  try {
    const raterUid = uid || auth?.currentUser?.uid;
    if (!contentId || !raterUid) return 0;
    const ref = doc(db, CONTENT_COL, String(contentId), RATINGS_SUBCOL, String(raterUid));
    const snap = await getDoc(ref);
    const v = snap.exists() ? Number(snap.data()?.value) : 0;
    const out = Number.isInteger(v) && v >= 1 && v <= 5 ? v : 0;
    // PR-63 — canonical KAZANIR: cache'i gerçek değerle hizala (varsa yaz,
    // canonical "puan yok" diyorsa stale cache girişini kaldır).
    if (out >= 1) setCachedMyRating(raterUid, contentId, out);
    else removeCachedMyRating(raterUid, contentId);
    return out;
  } catch {
    return 0;   // eski sözleşme: hata → 0 (ayrım için getMyRatingDurum)
  }
}

// -------------------- EMİR 03: snapshot error callback + perm-denied spam kırıcı --------------------
const __DEV__ = process.env.NODE_ENV !== "production";
const __aggSeen = new Set<string>();

function isPermDeniedLike(err: unknown) {
  try {
    const code = String(hataAlani(err, "code") || "").toLowerCase();
    const msg = String(hataAlani(err, "message") || err || "").toLowerCase();
    const t = `${code} ${msg}`;
    return t.includes("permission") && t.includes("denied");
  } catch {
    return false;
  }
}

function logAggOnce(contentId: string | null | undefined, err: unknown) {
  if (!__DEV__) return;
  try {
    const code = String(hataAlani(err, "code") || "unknown");
    const key = `content:agg:${String(contentId)}:${code}`;
    if (__aggSeen.has(key)) return;
    __aggSeen.add(key);
    // eslint-disable-next-line no-console
    console.warn("[content:agg] snapshot error", {
      contentId: String(contentId),
      code,
      message: String(hataAlani(err, "message") || err || ""),
    });
  } catch {}
}

function safeCall(cb: ToplamDinleyici | null | undefined, value: IcerikToplami | null) {
  try {
    cb?.(value);
  } catch {
    // no-op
  }
}
// -----------------------------------------------------------------------------------------------

/** İçerik aggregate canlı dinleme (UI eskisiyle uyumlu: agg.*) */
export function onContentAggregate(contentId: string | null | undefined, cb: ToplamDinleyici) {
  if (!contentId) return () => {};
  const ref = doc(db, CONTENT_COL, String(contentId));

  let unsub = () => {};

  try {
    unsub = onSnapshot(
      ref,
      (snap) => {
        // COMMUTATIVE GEÇİŞİ (2026-08-19): sunucu artık `agg.avg` YAZMIYOR
        // (avg = sum/count → bölme → toplamsal değil → `increment()` ile
        // yazılamaz; gerekçe: src/utils/aggTuret.ts başlığı +
        // docs/METASTABIL_COKUS_MEKANIZMASI.md). Ortalama BURADA türetilir →
        // `agg.avg` okuyan tüm tüketiciler (ClipsDesktop, MyClips,
        // ProfilePostViewerMobile, useClipEngagement) tek noktadan düzelir.
        safeCall(cb, snap.exists() ? aggNormalize(snap.data()?.agg) : null);
      },
      (err) => {
        // ✅ kritik: error callback yoksa "Uncaught Error in snapshot listener" olur
        logAggOnce(contentId, err);

        // permission-denied ise sessiz degrade + unsubscribe (retry/spam kır)
        if (isPermDeniedLike(err)) {
          safeCall(cb, null);
          try {
            unsub?.();
          } catch {}
          return;
        }

        // diğer hatalarda da UI çökmemeli
        safeCall(cb, null);
      }
    );
  } catch (err) {
    logAggOnce(contentId, err);
    safeCall(cb, null);
  }

  return () => {
    try {
      unsub?.();
    } catch {}
  };
}
